import { ActionBar, ActionBarButton, ActionBarLabel, ActionBarSeparator } from 'components/common/ActionBar'
import { AddOutlined, AutoAwesomeOutlined, CheckOutlined } from '@mui/icons-material'
import { FC, useId } from 'react'

import { ActionBarState } from 'utils/socialCampaign/schedule'
import { BottomBar } from 'components/common/BottomBar'
import { MUIButton } from 'components/common/MUIButton'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface Props {
  state: ActionBarState
  /**
   * An action is on its way, or section 1 is re-planning the drafts: Approve all, Launch and Generate wait (each would
   * act on drafts about to change).
   */
  busy: boolean
  /** Approve all, or Launch campaign once every draft is approved: both approve every draft and schedule them */
  onApproveAll: () => void
  /** Review all: the editor on the first draft still to decide (ISSUE-3624); disabled without it */
  onReviewAll?: () => void
  /** Add a post: the editor on a new post (ISSUE-3624); disabled without it */
  onAddPost?: () => void
  /** Generate new posts: its dialog */
  onGenerate: () => void
}

/**
 * The campaign's action bar (prototype v3 `renderBar`, v2 decisions 1 and 18): the floating dark `ActionBar` from the
 * `sm` breakpoint, a `BottomBar` on a phone. Drafts to decide: "0 of 7 approved", Approve all (approves and launches)
 * and Review all (primary), then Launch campaign once every draft is approved. Nothing left to decide once launched:
 * Add a post and Generate new posts. Both bars are portalled to <body>: the property page's transitions leave a
 * transform on the tab's ancestors, which would turn `position: fixed` into a scrolling one.
 */
export const CampaignActionBar: FC<Props> = ({ state, busy, onApproveAll, onReviewAll, onAddPost, onGenerate }) => {
  const { t } = useTranslation(['social_campaign'])
  const countId = `campaign-bar-${useId().replace(/:/g, '')}`

  if (typeof document === 'undefined') return null

  let desktop
  let phone
  if (state.kind === 'review') {
    const allApproved = state.approved === state.drafts
    desktop = (
      <>
        <ActionBarLabel id={countId}>{t('bar.approved', { approved: state.approved, count: state.drafts })}</ActionBarLabel>
        <ActionBarSeparator />
        {allApproved && <ActionBarButton label={t('bar.launch')} isPrimary icon={<CheckOutlined />} aria-describedby={countId} disabled={busy} onClick={onApproveAll} />}
        {!allApproved && <ActionBarButton label={t('bar.approve_all')} icon={<CheckOutlined />} aria-describedby={countId} disabled={busy} onClick={onApproveAll} />}
        {!allApproved && <ActionBarButton label={t('bar.review_all')} isPrimary aria-describedby={countId} disabled={!onReviewAll} onClick={onReviewAll} />}
      </>
    )
    phone = allApproved
      ? <MUIButton size="lg" startIcon={<CheckOutlined />} disabled={busy} onClick={onApproveAll}>{t('bar.launch')}</MUIButton>
      : (
        <>
          <MUIButton type="secondaryBorder" size="lg" disabled={busy} onClick={onApproveAll}>{t('bar.approve_all')}</MUIButton>
          <MUIButton size="lg" disabled={!onReviewAll} onClick={onReviewAll}>{t('bar.review_all')}</MUIButton>
        </>
      )
  } else {
    desktop = (
      <>
        <ActionBarButton label={t('bar.add_post')} icon={<AddOutlined />} disabled={!onAddPost} onClick={onAddPost} />
        <ActionBarSeparator />
        <ActionBarButton label={t('bar.generate')} isPrimary icon={<AutoAwesomeOutlined />} disabled={busy} onClick={onGenerate} />
      </>
    )
    phone = (
      <>
        {/* the glyph alone keeps its square: the bottom bar stretches its actions */}
        <MUIButton
          type="secondaryBorder"
          size="lg"
          iconOnly
          aria-label={t('bar.add_post')}
          disabled={!onAddPost}
          onClick={onAddPost}
          sx={{ '&&': { flex: 'none' } }}
        >
          <AddOutlined sx={{ fontSize: '2rem' }} />
        </MUIButton>
        <MUIButton size="lg" startIcon={<AutoAwesomeOutlined />} disabled={busy} onClick={onGenerate}>{t('bar.generate')}</MUIButton>
      </>
    )
  }

  return createPortal(
    <>
      <ActionBar role="toolbar" aria-label={t('bar.label')} isHiddenOnPhone>{desktop}</ActionBar>
      <BottomBar
        isFixed
        isPhoneOnly
        aria-label={t('bar.label')}
        label={state.kind === 'review' ? t('bar.approved_label') : undefined}
        value={state.kind === 'review' ? t('bar.approved_value', { approved: state.approved, count: state.drafts }) : undefined}
        actions={phone}
      />
    </>,
    document.body
  )
}
